import isNil from 'lodash/isNil'
import { FC, ReactNode, useRef } from 'react'
import { NavLink, NavLinkProps } from 'react-router-dom'

import useHover from 'src/hooks/useHover'
import { useId } from 'src/hooks/useId'
import { cn } from 'src/lib/cn'
import { SIDEBAR_MENU_HOVER_LEAVE_DELAY } from 'src/utils/constants/SidebarMenu'
import ItemWithTooltip from '../../molecules/ItemWithTooltip'
import DropdownMenuItem, { DropdownMenuItemProps } from '../DropdownMenuItem/DropdownMenuItem'
import LaunchingLink from '../LaunchingLink'
import SidebarMenuItemProvider from './SidebarMenuItemProvider'

const OFFSET = 20

export interface SidebarMenuItemProps extends Omit<DropdownMenuItemProps, 'href'> {
  to?: string
  menu?: {
    isOpen: boolean
    content: ReactNode
    className?: string
    openOnHover?: boolean
    isBottomToTop?: boolean
    minLeft?: number
    delay?: number
    style?: React.CSSProperties
  }
  containerClassName?: string
  isVisible?: boolean
  withLaunchingLink?: boolean
  isOnModal?: boolean // Color should be blue on active
  showActiveIndicator?: boolean // Show a blue line on the left of the item
  showOpenInNewTabIcon?: boolean // Show the icon to open the link in a new tab when hovering
  href?: string
}

const SidebarMenuItem: FC<SidebarMenuItemProps> = ({
  className,
  label,
  active,
  classes,
  addon,
  to,
  'aria-label': ariaLabel,
  menu,
  containerClassName,
  onMouseEnter,
  onMouseLeave,
  isVisible,
  'data-testid': dataTestId = 'sidebar-menu-item',
  target,
  rightIcon,
  withLaunchingLink = true,
  isOnModal,
  showActiveIndicator = true,
  showOpenInNewTabIcon = true,
  ...otherProps
}) => {
  const isLink = otherProps.disabled ? false : Boolean(to)
  const Wrapper = isLink ? NavLink : 'div'
  const navProps: NavLinkProps | object = isLink ? { to, target } : {}
  const dropdownRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const tooltipId = useId()

  const containerRef = useRef<HTMLDivElement>(null)

  const {
    hoverProps,
    isHovered: isHoveredArg,
    delayedHover,
    leaveHover,
  } = useHover({
    onMouseMove: onMouseEnter,
    onMouseLeave,
    mouseEnterDelay: menu?.delay,
    mouseLeaveDelay: SIDEBAR_MENU_HOVER_LEAVE_DELAY,
    mouseLeaveActiveDirection: 'right',
  })

  const isHovered = isHoveredArg && !otherProps.disabled

  const isMenuOpened = Boolean(
    menu?.isOpen || (menu?.openOnHover ? (menu?.openOnHover ? delayedHover : true) : false),
  )

  const boundingRectangle = containerRef.current?.getBoundingClientRect()

  const left = Math.max(boundingRectangle?.right ?? 0, menu?.minLeft ?? 0)
  const top = boundingRectangle?.top ?? 0
  const bottom = boundingRectangle?.bottom ?? 0

  const maxHeight = menu?.isBottomToTop ? top - OFFSET : window.innerHeight - top - OFFSET

  if (!isNil(isVisible) && !isVisible) {
    return null
  }

  const menuStyle = menu?.style

  return (
    <SidebarMenuItemProvider closeHover={leaveHover}>
      <div {...hoverProps} className={cn('w-full', containerClassName)} ref={containerRef}>
        <Wrapper
          {...(navProps as any)}
          aria-label={ariaLabel || label}
          className={cn('relative block w-full')}
          ref={dropdownRef}
        >
          {active && showActiveIndicator && (
            <div
              data-testid="sidebar-menu-item-active-indicator"
              className={cn('bg-bay-of-many absolute inset-0 h-full w-0.5', isOnModal && 'bg-blue')}
            />
          )}
          <DropdownMenuItem
            data-testid={dataTestId}
            role="button"
            classes={{
              ...classes,
              icon: cn('text-disabled-grey', active && 'text-bay-of-many!', classes?.icon),
              rightIcon: cn(
                'mr-0 ml-auto',
                active && 'text-bay-of-many',
                addon && 'mx-0',
                classes?.rightIcon,
              ),
              label: cn(
                'whitespace-nowrap text-ellipsis text-sm text-dark-grey inline-block overflow-hidden',
                isHovered && 'text-outer-space font-semibold',
                active && 'text-bay-of-many font-semibold',
                isOnModal && active && 'text-blue',
                classes?.label,
              ),
              addon: cn('mx-0 ml-auto', classes?.addon),
            }}
            aria-label={isLink ? undefined : ariaLabel}
            className={cn(
              'h-auto gap-2 px-4 py-1 text-ellipsis',
              isHovered && 'bg-foam',
              className,
            )}
            label={label}
            addon={addon}
            isHovered={isMenuOpened ? true : undefined}
            rightIcon={
              (rightIcon || (to && isHovered && withLaunchingLink)) && (
                <div className="flex items-center gap-2">
                  {rightIcon}
                  {to && showOpenInNewTabIcon && isHovered && withLaunchingLink && (
                    <ItemWithTooltip
                      id={tooltipId}
                      item={<LaunchingLink to={to} className={cn('text-shuttle-gray')} />}
                    >
                      Open in a new tab
                    </ItemWithTooltip>
                  )}
                </div>
              )
            }
            {...otherProps}
          />
        </Wrapper>
        {isMenuOpened && (
          <div
            data-testid="sidebar-menu-item-menu"
            className={cn(
              'shadow-popup fixed ml-1 flex min-w-[190px] flex-col rounded-lg bg-white py-2',
              menu?.isBottomToTop && 'translate-y-[-100%]',
              menu?.className,
            )}
            role="menu"
            ref={menuRef}
            style={{
              left,
              top: !menu?.isBottomToTop ? top : bottom,
              ...menuStyle,
            }}
          >
            <div
              className="relative max-h-100 overflow-x-hidden overflow-y-auto rounded-lg"
              style={{
                maxHeight,
              }}
            >
              {menu?.content}
            </div>
          </div>
        )}
      </div>
    </SidebarMenuItemProvider>
  )
}

export default SidebarMenuItem
